import React from 'react';
import { SensorData } from '../types';
import './SensorGauges.css';

interface SensorGaugesProps {
  sensorData: SensorData[];
}

const SensorGauges: React.FC<SensorGaugesProps> = ({ sensorData }) => {
  const getSensorByType = (type: string) => {
    return sensorData.find((s) => s.sensor_type === type);
  };

  const temperature = getSensorByType('temperature');
  const humidity = getSensorByType('humidity');
  const airQuality = getSensorByType('air_quality');

  const getAQIColor = (value: number) => {
    if (value <= 50) return '#27ae60'; // Good
    if (value <= 100) return '#f39c12'; // Moderate
    if (value <= 150) return '#e67e22'; // Unhealthy for Sensitive
    if (value <= 200) return '#e74c3c'; // Unhealthy
    return '#8e44ad'; // Very Unhealthy
  };

  const getAQILabel = (value: number) => {
    if (value <= 50) return 'Good';
    if (value <= 100) return 'Moderate';
    if (value <= 150) return 'Unhealthy for Sensitive';
    if (value <= 200) return 'Unhealthy';
    return 'Very Unhealthy';
  };

  return (
    <div className="sensor-gauges-card">
      <div className="gauges-header">
        <div className="gauges-title">
          <svg className="gauges-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="currentColor"/>
            <path d="M12.5 7H11V13L16.2 16.2L17 14.9L12.5 12.2V7Z" fill="currentColor"/>
          </svg>
          <h3>Environmental Sensors</h3>
        </div>
        {sensorData.length > 0 && sensorData[0].is_simulated && (
          <span className="simulated-badge">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Simulated Data
          </span>
        )}
      </div>

      <div className="gauges-grid">
        {/* Temperature Gauge */}
        <div className="gauge-item">
          <div className="gauge-header-row">
            <svg className="gauge-icon-svg" width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 14.76V3.5C14 2.67 13.33 2 12.5 2C11.67 2 11 2.67 11 3.5V14.76C9.71 15.53 9 16.95 9 18.5C9 20.71 10.79 22.5 13 22.5C15.21 22.5 17 20.71 17 18.5C17 16.95 16.29 15.53 15 14.76Z" fill="#e74c3c"/>
            </svg>
            <div className="gauge-label">Temperature</div>
          </div>
          <div className="gauge-value">
            {temperature ? `${temperature.value}°${temperature.unit}` : 'N/A'}
          </div>
          {temperature && (
            <div className="gauge-timestamp">
              {new Date(temperature.timestamp).toLocaleTimeString('en-US', { timeZone: 'America/Mexico_City', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
          )}
        </div>

        {/* Humidity Gauge */}
        <div className="gauge-item">
          <div className="gauge-header-row">
            <svg className="gauge-icon-svg" width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2.69L17.66 8.35C18.78 9.47 19.34 10.83 19.34 12.19C19.34 15.07 17 17.41 14.12 17.41C11.24 17.41 8.9 15.07 8.9 12.19C8.9 10.83 9.46 9.47 10.58 8.35L12 2.69Z" fill="#3498db"/>
            </svg>
            <div className="gauge-label">Humidity</div>
          </div>
          <div className="gauge-value">
            {humidity ? `${humidity.value}${humidity.unit}` : 'N/A'}
          </div>
          {humidity && (
            <div className="gauge-timestamp">
              {new Date(humidity.timestamp).toLocaleTimeString('en-US', { timeZone: 'America/Mexico_City', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
          )}
        </div>

        {/* Air Quality Gauge */}
        <div className="gauge-item">
          <div className="gauge-header-row">
            <svg className="gauge-icon-svg" width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 9L15 14H13.5L14.5 9H14ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM12 18C10.34 18 9 16.66 9 15C9 13.34 10.34 12 12 12C13.66 12 15 13.34 15 15C15 16.66 13.66 18 12 18Z" fill={airQuality ? getAQIColor(airQuality.value) : '#95a5a6'}/>
            </svg>
            <div className="gauge-label">Air Quality</div>
          </div>
          <div
            className="gauge-value"
            style={{ color: airQuality ? getAQIColor(airQuality.value) : '#636e72' }}
          >
            {airQuality ? Math.round(airQuality.value) : 'N/A'}
          </div>
          {airQuality && (
            <>
              <div className="aqi-label" style={{ color: getAQIColor(airQuality.value) }}>
                {getAQILabel(airQuality.value)}
              </div>
              <div className="gauge-timestamp">
                {new Date(airQuality.timestamp).toLocaleTimeString('en-US', { timeZone: 'America/Mexico_City', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </div>
            </>
          )}
        </div>
      </div>

      {sensorData.length === 0 && (
        <div className="no-sensor-data">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#e1e8ed" strokeWidth="2"/>
            <path d="M12 8V12" stroke="#95a5a6" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="12" cy="16" r="1" fill="#95a5a6"/>
          </svg>
          <p>No sensor data available</p>
          <small>Waiting for device to send readings...</small>
        </div>
      )}
    </div>
  );
};

export default SensorGauges;
